import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "../ui/table";
import { Skeleton } from "../ui/skeleton";
import { StatusBadge } from "../shared/StatusBadge";
import { EmptyState } from "../shared/EmptyState";
import { PushReceiptsDialog } from "./PushReceiptsDialog";
import { apiClient, getApiErrorMessage } from "../../lib/apiClient";
import { formatDate } from "../../lib/utils";
import { useListState } from "../../hooks/useListState";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/v2`;

function dispatchMeta(sent) {
  return sent ? { label: "Sent", variant: "success" } : { label: "Not Delivered", variant: "warning" };
}

function formatTs(ts) {
  if (!ts) return "—";
  const d = /^\d+$/.test(String(ts)) ? new Date(Number(ts) * 1000) : new Date(ts);
  return isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

function BroadcastThumb({ image }) {
  const [broken, setBroken] = useState(false);
  if (!image || broken) return "—";
  return (
    <img
      src={`${API_BASE}/daily-menu/images/${image}`} alt=""
      className="h-10 w-14 rounded-md border border-border object-cover"
      onError={() => setBroken(true)}
    />
  );
}

export function NotificationHistoryTab() {
  const [broadcasts, setBroadcasts] = useState({ loading: true, rows: [] });
  const [log, setLog] = useState({ loading: true, rows: [] });
  const [customerFilter, setCustomerFilter] = useListState("notifications.historyCustomerFilter", "");
  const [receipts, setReceipts] = useState({ open: false, correlationId: null, title: "" });

  const loadBroadcasts = () => {
    setBroadcasts((s) => ({ ...s, loading: true }));
    apiClient.get("/notifications/broadcasts", { params: { limit: 50 } })
      .then(({ data }) => setBroadcasts({ loading: false, rows: data.items }))
      .catch((error) => { toast.error(getApiErrorMessage(error, "Could not load broadcast history.")); setBroadcasts({ loading: false, rows: [] }); });
  };

  const loadLog = () => {
    setLog((s) => ({ ...s, loading: true }));
    apiClient.get("/notifications/log", { params: { customer_id: customerFilter || undefined, limit: 50 } })
      .then(({ data }) => setLog({ loading: false, rows: data.items }))
      .catch((error) => { toast.error(getApiErrorMessage(error, "Could not load the dispatch log.")); setLog({ loading: false, rows: [] }); });
  };

  useEffect(loadBroadcasts, []);
  useEffect(loadLog, [customerFilter]);

  return (
    <div className="mt-4 space-y-6" data-testid="notification-history-tab">
      <Card data-testid="broadcast-history-card">
        <CardContent className="p-0">
          <p className="p-4 pb-2 font-heading text-sm font-semibold text-text-main">Broadcast History</p>
          {broadcasts.loading ? <Skeleton className="m-4 h-32" /> : broadcasts.rows.length === 0 ? (
            <EmptyState title="No broadcasts sent yet" testId="broadcast-history-empty" />
          ) : (
            <Table data-testid="broadcast-history-table">
              <TableHeader>
                <TableRow><TableHead>Photo</TableHead><TableHead>Date</TableHead><TableHead>Message</TableHead><TableHead>Sent At</TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {broadcasts.rows.map((b) => (
                  <TableRow key={b.id} data-testid={`broadcast-history-row-${b.id}`}>
                    <TableCell>
                      <BroadcastThumb image={b.image} />
                    </TableCell>
                    <TableCell>{b.date ? formatDate(b.date) : "—"}</TableCell>
                    <TableCell className="max-w-sm truncate">{b.comment}</TableCell>
                    <TableCell>{b.created_at ? new Date(Number(b.created_at) * 1000).toLocaleString() : "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card data-testid="dispatch-log-card">
        <CardContent className="p-0">
          <div className="flex items-center justify-between p-4 pb-2">
            <p className="font-heading text-sm font-semibold text-text-main">Dispatch Log</p>
            <div className="space-y-1">
              <Label htmlFor="dispatch-log-customer-filter" className="text-xs">Filter by Customer ID</Label>
              <Input
                id="dispatch-log-customer-filter" type="number" placeholder="e.g. 42" className="h-8 w-32"
                value={customerFilter} onChange={(e) => setCustomerFilter(e.target.value)} data-testid="dispatch-log-customer-filter-input"
              />
            </div>
          </div>
          {log.loading ? <Skeleton className="m-4 h-32" /> : log.rows.length === 0 ? (
            <EmptyState title="No dispatch activity yet" testId="dispatch-log-empty" />
          ) : (
            <Table data-testid="dispatch-log-table">
              <TableHeader>
                <TableRow><TableHead>Event</TableHead><TableHead>Recipient</TableHead><TableHead>Title</TableHead><TableHead>Status</TableHead><TableHead>Sent</TableHead><TableHead>Receipts</TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {log.rows.map((r, i) => {
                  const meta = dispatchMeta(r.sent);
                  return (
                    <TableRow key={i} data-testid={`dispatch-log-row-${i}`}>
                      <TableCell>{r.event}</TableCell>
                      <TableCell>{r.recipient_customer_id ? `Customer #${r.recipient_customer_id}` : r.topic || "—"}</TableCell>
                      <TableCell className="max-w-xs truncate">{r.title}</TableCell>
                      <TableCell><StatusBadge label={meta.label} variant={meta.variant} /></TableCell>
                      <TableCell>{formatTs(r.ts)}</TableCell>
                      <TableCell>
                        {r.correlation_id ? (
                          <Button
                            variant="outline" size="sm" className="h-7 gap-1.5 text-xs"
                            onClick={() => setReceipts({ open: true, correlationId: r.correlation_id, title: r.title })}
                            data-testid={`dispatch-log-receipts-button-${i}`}
                          >
                            <Users size={13} strokeWidth={1.5} />
                            View{typeof r.recipients === "number" ? ` (${r.recipients})` : ""}
                          </Button>
                        ) : "—"}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <PushReceiptsDialog
        correlationId={receipts.correlationId}
        title={receipts.title}
        open={receipts.open}
        onOpenChange={(v) => setReceipts((s) => ({ ...s, open: v }))}
      />
    </div>
  );
}
